/**
* แปลและรับรองเอกสารทุกภาษาเพื่อใช้ในประเทศไทย (Multi-Language Translation
* & Legalization) — 500+ keyword universe.
*
* Derived from the client's curated "แปลและรับรองเอกสารทุกภาษา" dataset:
* language pairs, document types (civil / corporate / legal / medical /
* technical), certification routes, and long-tail advisory phrases.
*
* Consumed by /translation/questions, /translation/q/$slug, AI JSONL feeds
* and sitemaps.
*/

export type MltKeywordGroup = {
  id: string;
  label: string;
  keywords: string[];
};

/** Language universe — powers pair keywords and the Q&A matrix. */
export const MLT_LANGUAGES = [
  { slug: "english", th: "อังกฤษ", en: "English" },
  { slug: "chinese", th: "จีน", en: "Chinese" },
  { slug: "japanese", th: "ญี่ปุ่น", en: "Japanese" },
  { slug: "korean", th: "เกาหลี", en: "Korean" },
  { slug: "german", th: "เยอรมัน", en: "German" },
  { slug: "french", th: "ฝรั่งเศส", en: "French" },
  { slug: "spanish", th: "สเปน", en: "Spanish" },
  { slug: "russian", th: "รัสเซีย", en: "Russian" },
  { slug: "italian", th: "อิตาลี", en: "Italian" },
  { slug: "arabic", th: "อาหรับ", en: "Arabic" },
  { slug: "vietnamese", th: "เวียดนาม", en: "Vietnamese" },
  { slug: "myanmar", th: "พม่า", en: "Myanmar" },
  { slug: "lao", th: "ลาว", en: "Lao" },
  { slug: "khmer", th: "เขมร", en: "Khmer" },
  { slug: "indonesian", th: "อินโดนีเซีย", en: "Indonesian" },
  { slug: "malay", th: "มาเลย์", en: "Malay" },
  { slug: "tagalog", th: "ตากาล็อก", en: "Tagalog" },
  { slug: "dutch", th: "ดัตช์", en: "Dutch" },
  { slug: "swedish", th: "สวีเดน", en: "Swedish" },
  { slug: "danish", th: "เดนมาร์ก", en: "Danish" },
  { slug: "finnish", th: "ฟินแลนด์", en: "Finnish" },
  { slug: "norwegian", th: "นอร์เวย์", en: "Norwegian" },
  { slug: "hindi", th: "ฮินดี", en: "Hindi" },
  { slug: "persian", th: "เปอร์เซีย", en: "Persian" },
  { slug: "turkish", th: "ตุรกี", en: "Turkish" },
  { slug: "portuguese", th: "โปรตุเกส", en: "Portuguese" },
  { slug: "polish", th: "โปแลนด์", en: "Polish" },
  { slug: "czech", th: "เช็ก", en: "Czech" },
  { slug: "hebrew", th: "ฮีบรู", en: "Hebrew" },
  { slug: "nepali", th: "เนปาล", en: "Nepali" },
] as const;

/** Document types used by the Q&A matrix generator. */
export const MLT_DOC_TYPES = [
  { slug: "birth-certificate", th: "สูติบัตร", en: "birth certificate" },
  { slug: "marriage-certificate", th: "ทะเบียนสมรส", en: "marriage certificate" },
  { slug: "divorce-certificate", th: "ใบหย่า", en: "divorce certificate" },
  { slug: "death-certificate", th: "ใบมรณบัตร", en: "death certificate" },
  { slug: "name-change", th: "ใบเปลี่ยนชื่อ-สกุล", en: "name change certificate" },
  { slug: "house-registration", th: "ทะเบียนบ้าน", en: "house registration" },
  { slug: "police-clearance", th: "ใบตรวจประวัติอาชญากรรม", en: "police clearance certificate" },
  { slug: "degree", th: "ปริญญาบัตร", en: "degree certificate" },
  { slug: "transcript", th: "ทรานสคริปต์", en: "academic transcript" },
  { slug: "passport", th: "หนังสือเดินทาง", en: "passport" },
  { slug: "company-affidavit", th: "หนังสือรับรองบริษัท", en: "company affidavit" },
  { slug: "financial-statement", th: "งบการเงิน", en: "financial statement" },
  { slug: "shareholder-list", th: "บัญชีรายชื่อผู้ถือหุ้น (บอจ.5)", en: "shareholder list" },
  { slug: "articles-of-association", th: "ข้อบังคับบริษัท", en: "articles of association" },
  { slug: "business-license", th: "ใบอนุญาตประกอบธุรกิจ", en: "business license" },
  { slug: "commercial-contract", th: "สัญญาทางการค้า", en: "commercial contract" },
  { slug: "power-of-attorney", th: "หนังสือมอบอำนาจ", en: "power of attorney" },
  { slug: "court-judgment", th: "คำพิพากษาศาล", en: "court judgment" },
  { slug: "medical-certificate", th: "ใบรับรองแพทย์", en: "medical certificate" },
  { slug: "vaccination-record", th: "สมุดวัคซีน", en: "vaccination record" },
  { slug: "driving-license", th: "ใบขับขี่", en: "driving licence" },
  { slug: "bank-statement", th: "สเตทเมนต์ธนาคาร", en: "bank statement" },
  { slug: "employment-contract", th: "สัญญาจ้างงาน", en: "employment contract" },
  { slug: "insurance-policy", th: "กรมธรรม์ประกันภัย", en: "insurance policy" },
  { slug: "will", th: "พินัยกรรม", en: "will" },
] as const;

/** 1) Core multi-language service terms */
const CORE = [
  "แปลเอกสารทุกภาษาใช้ในไทย", "translation services for all languages thailand",
  "แปลและรับรองเอกสารเพื่อใช้ในไทย", "certified translation and legalization thailand",
  "ศูนย์แปลเอกสารทุกภาษากรุงเทพ", "multi-language translation agency bangkok",
  "แปลเอกสารราชการทุกภาษา", "official document translation all languages",
  "ศูนย์แปลภาษาต่างประเทศครบวงจร", "comprehensive foreign translation center",
  "ร้านแปลเอกสารใกล้กรมการกงสุล", "translation agency near mfa bangkok",
  "บริการแปลเอกสารด่วนทุกภาษา", "urgent multi-language translation thailand",
  "แปลเอกสารพร้อมรับรอง Notary Public", "notarized translation services thailand",
  "แปลเอกสารพร้อมรับรองกระทรวงการต่างประเทศ", "mfa legalized translation thailand",
  "นักแปลที่ขึ้นทะเบียนกรมการกงสุล", "mfa accredited translators thailand",
  "บริการรับแปลเอกสารออนไลน์ทุกภาษา", "online translation services all languages thailand",
  "ส่งต้นฉบับแปลเอกสารผ่านอีเมลและไลน์", "email and line document translation submission",
  "ค่าบริการแปลเอกสารทุกภาษาคิดต่อหน้า", "translation rates per page in thailand",
  "แปลเอกสารด่วน 24 ชั่วโมงทุกภาษา", "24-hour express translation services thailand",
  "แปลเอกสารโดยเจ้าของภาษา", "native speaker document translation thailand",
  "แปลเอกสารพร้อมประทับตรารับรองบริษัท", "company certified stamp translation thailand",
  "แปลเอกสารสองภาษาแบบคู่ขนาน", "bilingual parallel column translation thailand",
  "รับแปลเอกสารพร้อมจัดส่ง EMS ทั่วไทย", "nationwide courier for translated documents",
  "แปลเอกสารพร้อมรับรองสถานทูต", "embassy certified translation thailand",
  "แปลเอกสารรับรองคำแปลถูกต้อง", "certificate of translation accuracy thailand",
];

/** 2) Language pair keywords (auto-expanded) */
const PAIRS = MLT_LANGUAGES.flatMap((l) => [
  `แปลภาษา${l.th}เป็นไทยพร้อมรับรอง`,
  `${l.en.toLowerCase()} to thai certified translation`,
  `แปลไทยเป็นภาษา${l.th}พร้อมรับรอง`,
  `thai to ${l.en.toLowerCase()} certified translation`,
]);

/** 3) Document-type keywords (auto-expanded) */
const DOCS = MLT_DOC_TYPES.flatMap((d) => [
  `แปล${d.th}ทุกภาษาใช้ในไทย`,
  `multi-language ${d.en} translation thailand`,
]);

/** 4) Certification-route intents */
const CERTIFICATION = [
  "แปลเอกสารต่างประเทศเพื่อยื่นอำเภอ", "foreign document translation for thai district office",
  "แปลเอกสารเพื่อยื่นสำนักงานตรวจคนเข้าเมือง", "translation for thai immigration bureau",
  "แปลเอกสารเพื่อยื่นกรมพัฒนาธุรกิจการค้า", "translation for dbd company registration thailand",
  "แปลเอกสารเพื่อยื่นศาลไทย", "translation for thai court submission",
  "แปลเอกสารเพื่อยื่นกรมที่ดิน", "translation for thai land department",
  "แปลเอกสารเพื่อขอใบอนุญาตทำงาน", "translation for thai work permit application",
  "แปลเอกสารเพื่อขอวีซ่าติดตามคู่สมรสไทย", "translation for thai marriage visa",
  "แปลเอกสารเพื่อจดทะเบียนสมรสในไทย", "translation for marriage registration in thailand",
  "แปลเอกสารเพื่อรับบุตรบุญธรรมในไทย", "translation for child adoption in thailand",
  "แปลเอกสารเพื่อขอสัญชาติไทย", "translation for thai citizenship application",
  "ลำดับขั้นตอนแปล-โนตารี-กงสุล", "translation notary legalization order thailand",
  "แปลเอกสารพร้อม Apostille", "apostille translation service thailand",
  "แปลเอกสารต่างชาติรับรองสถานทูตในไทย", "foreign embassy certified translation in bangkok",
  "แปลเอกสารจากประเทศภาคีเฮก", "hague member state document translation thailand",
  "แปลเอกสารจากประเทศนอกภาคีเฮก", "non-hague country document translation thailand",
];

/** 5) Specialized / technical translation */
const SPECIALIZED = [
  "แปลคู่มือเครื่องจักรอุตสาหกรรม", "industrial machinery manual translation thailand",
  "แปลเอกสารสิทธิบัตรและอนุสิทธิบัตร", "patent document translation thailand",
  "แปลรายงานการตรวจสอบบัญชีประจำปี", "annual audit report translation thailand",
  "แปลเอกสารประกวดราคาภาครัฐ", "government tender document translation thailand",
  "แปลรายงานผลกระทบสิ่งแวดล้อม EIA", "eia environmental report translation thailand",
  "แปลเอกสารทางการแพทย์และผลแล็บ", "medical report and lab result translation thailand",
  "แปลเอกสารขึ้นทะเบียนยา อย.", "fda drug registration document translation thailand",
  "แปลเอกสารมาตรฐาน ISO", "iso standard document translation thailand",
  "แปลเอกสารคดีอนุญาโตตุลาการระหว่างประเทศ", "international arbitration document translation",
  "แปลสัญญาแฟรนไชส์ระหว่างประเทศ", "international franchise agreement translation",
  "แปลเอกสารกองทุนรวมและหลักทรัพย์", "mutual fund and securities document translation",
  "แปลเอกสารประกันภัยและสินไหม", "insurance and claim document translation thailand",
  "แปลเอกสารศุลกากรและนำเข้าส่งออก", "customs and import export document translation",
  "แปลเอกสารการเดินเรือพาณิชย์", "commercial shipping document translation thailand",
  "แปลเอกสารการบินและซ่อมบำรุงอากาศยาน", "aviation maintenance manual translation",
];

/** 6) Long-tail advisory & problem solving */
const LONGTAIL = [
  "แปลชื่อไม่ตรงพาสปอร์ตต้องแก้ยังไง", "name spelling mismatch on certified translation",
  "เอกสารแปลถูกปฏิเสธจากราชการไทยทำยังไง", "why thai authorities reject translated documents",
  "ต้องใช้ต้นฉบับหรือสำเนาในการแปล", "original or copy required for certified translation",
  "อายุใช้งานของคำแปลรับรองกี่เดือน", "validity period of certified translation thailand",
  "แปลเอกสารเก่าที่อ่านไม่ชัดทำได้ไหม", "translating faded or illegible old documents",
  "ต่างกันยังไงแปลรับรองกับแปลธรรมดา", "certified vs standard translation difference",
  "ต่างกันยังไง Notary กับ Sworn Translator", "notary vs sworn translator difference thailand",
  "แก้คำผิดหลังประทับตรากงสุลได้ไหม", "correcting errors after mfa stamping",
  "แปลเอกสารภาษาที่หายากหาที่ไหน", "rare language translation provider thailand",
  "แปลเอกสารพร้อมถอดเสียงชื่อเป็นไทยถูกต้อง", "thai transliteration standard for foreign names",
  "แปลเอกสารหลายภาษาขอใบเสนอราคา", "multi-language translation quotation thailand",
  "แปลเอกสารรับรองมีผลทางกฎหมายแค่ไหน", "legal validity of certified translation thailand",
  "จ้างแปลออนไลน์ปลอดภัยไหมข้อมูลรั่วหรือเปล่า", "data privacy for online translation service",
  "แปลเอกสารเป็นชุดหลายภาษาพร้อมกัน", "batch multi-language translation package thailand",
  "ขอใบเสนอราคาแปลเอกสารฟรี", "free translation quotation thailand",
];

export const MLT_KEYWORDS_500: MltKeywordGroup[] = [
  { id: "core", label: "บริการหลัก / ศูนย์แปลครบวงจร", keywords: CORE },
  { id: "pairs", label: "คู่ภาษา (30 ภาษา)", keywords: PAIRS },
  { id: "documents", label: "ประเภทเอกสาร", keywords: DOCS },
  { id: "certification", label: "การรับรอง / หน่วยงานปลายทาง", keywords: CERTIFICATION },
  { id: "specialized", label: "งานแปลเฉพาะทาง", keywords: SPECIALIZED },
  { id: "longtail", label: "ปัญหา / คำแนะนำเชิงลึก", keywords: LONGTAIL },
];

export const MLT_KEYWORDS_FLAT: string[] = MLT_KEYWORDS_500.flatMap((g) => g.keywords);
export const MLT_KEYWORDS_TOTAL = MLT_KEYWORDS_FLAT.length;
